import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";

function Counter({ end, duration = 2, suffix = "", prefix = "" }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    if (isInView) {
      let startTime = null;
      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);
        const currentCount = Math.floor(progress * end);
        setCount(currentCount);
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [isInView, end, duration]);

  return (
    <motion.span
      ref={ref}
      initial={{ opacity: 0, scale: 0.5 }}
      whileInView={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-400 dark:to-purple-400 bg-clip-text text-transparent"
    >
      {prefix}{count}{suffix}
    </motion.span>
  );
}

const stats = [
  {
    number: 50,
    suffix: "+",
    label: "Projects Completed",
    icon: "🚀",
    color: "from-blue-500 to-cyan-500"
  },
  {
    number: 30,
    suffix: "+",
    label: "Happy Clients",
    icon: "😊",
    color: "from-green-500 to-emerald-500"
  },
  {
    number: 95,
    suffix: "%",
    label: "Client Satisfaction",
    icon: "⭐",
    color: "from-yellow-500 to-orange-500"
  },
  {
    number: 24,
    suffix: "/7",
    label: "Support Available",
    icon: "🛠️",
    color: "from-purple-500 to-pink-500"
  }
];

export default function StatsCounter({ dark }) {
  return (
    <section className="relative py-20 px-4 overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-cyan-50 dark:from-indigo-900/20 dark:via-[#1b1c4e]/30 dark:to-cyan-900/20" />
      
      <div className="relative max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-[#1b1c4e] dark:text-white mb-4">
            Our Impact in Numbers
          </h2>
          <p className="text-lg text-[#23244d] dark:text-white/80 max-w-2xl mx-auto">
            Delivering exceptional results and building lasting partnerships
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="text-center group"
            >
              <motion.div
                className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br flex items-center justify-center text-2xl shadow-lg group-hover:scale-110 transition-transform duration-300"
                style={{
                  background: `linear-gradient(135deg, var(--tw-gradient-stops))`,
                  '--tw-gradient-from': stat.color.split('-')[1] === '500' ? '#3b82f6' : '#60a5fa',
                  '--tw-gradient-to': stat.color.split('-')[3] === '500' ? '#06b6d4' : '#22d3ee'
                }}
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.6, ease: "easeInOut" }}
              >
                {stat.icon}
              </motion.div>
              
              <div className="mb-2">
                <Counter 
                  end={stat.number} 
                  suffix={stat.suffix}
                  duration={2}
                />
              </div>
              
              <h3 className="text-lg font-semibold text-[#1b1c4e] dark:text-white">
                {stat.label}
              </h3>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
} 